import React, { useState, useRef } from 'react';
import { getOfflineMap, updateOfflineMap } from '../../utils/offlineDb';

function OfflineBackup({ mapId, user }) {
  const [isBackingUp, setIsBackingUp] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [message, setMessage] = useState('');
  const fileInputRef = useRef();

  async function fetchMapData() {
    try {
      const data = await getOfflineMap(mapId);
      return data;
    } catch (e) {
      throw new Error('Failed to get offline map data');
    }
  }

  async function backupAsJson() {
    setIsBackingUp(true);
    setMessage('');
    try {
      const mapData = await fetchMapData();

      // Remove image/blob data before backup
      if (mapData.image) {
        delete mapData.image;
      }
      if (mapData.image_url) {
        delete mapData.image_url;
      }

      const jsonStr = JSON.stringify(mapData, null, 2);
      const blob = new Blob([jsonStr], { type: 'application/json' });

      // Ask user where to save the file via file system API if available
      if (window.showSaveFilePicker) {
        try {
          const handle = await window.showSaveFilePicker({
            suggestedName: `${mapData?.meta?.name || mapData?.name || 'map'}-backup.json`,
            types: [{
              description: 'JSON Files',
              accept: { 'application/json': ['.json'] },
            }],
          });
          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          setMessage('Backup saved successfully.');
        } catch (e) {
          setMessage('Save cancelled or failed: ' + e.message);
        }
      } else {
        // Fallback: download via anchor element
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${mapData?.meta?.name || mapData?.name || 'map'}-backup.json`;
        a.click();
        URL.revokeObjectURL(a.href);
        setMessage('Backup created and download started.');
      }
    } catch (e) {
      setMessage('Failed to create backup: ' + e.message);
    }
    setIsBackingUp(false);
  }

  async function handleRestoreFileChosen(e) {
    setMessage('');
    setIsRestoring(true);
    try {
      const file = e.target.files[0];
      if (!file) {
        setMessage('No file selected');
        setIsRestoring(false);
        return;
      }

      const text = await file.text();
      const mapData = JSON.parse(text);

      await updateOfflineMap(mapId, () => mapData);

      setMessage('Restore completed successfully. Please upload the map image separately.');
    } catch (e) {
      setMessage('Failed to restore backup: ' + e.message);
    }
    setIsRestoring(false);
    e.target.value = null;
  }

  function handleBackupClick() {
    setMessage('');
    backupAsJson();
  }

  function handleRestoreClick() {
    setMessage('');
    fileInputRef.current?.click();
  }

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <button
        className="map-controls-btn"
        onClick={handleBackupClick}
        disabled={isBackingUp}
        title="Backup map data (without image)"
        style={{ padding: '6px 10px' }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
      </button>

      <input
        ref={fileInputRef}
        type="file"
        accept=".json,application/json"
        style={{ display: 'none' }}
        onChange={handleRestoreFileChosen}
      />

      {message && (
        <div style={{ color: '#ffe089', fontWeight: '600', marginTop: 4 }}>
          {message}
        </div>
      )}
    </div>
  );
}

export default OfflineBackup;
